// ═══════════════════════════════════════════════════════════════════════════════
// Care Feature-Gate Types
// Parallele Struktur zu practice PracticeFeatureKey / PLAN_FEATURES
// ═══════════════════════════════════════════════════════════════════════════════

export type CareSubscriptionPlan = 'none' | 'starter' | 'professional' | 'enterprise';

export type CareFeatureKey =
    | 'careStation'
    | 'careResidents'
    | 'careHandover'
    | 'careRoster'
    | 'careInventory'
    | 'careInbox'
    | 'careWoundMgmt'
    | 'careMedications'
    | 'careSisDocumentation'
    | 'careAdmissions'
    | 'careOccupancy'
    | 'careContacts'
    | 'carePracticeRequests'
    | 'careFamilyPortal'
    | 'careAnalytics';

export type CareFeatures = Record<CareFeatureKey, boolean>;

/** camelCase → snake_case für DB */
export const CARE_FEATURE_KEY_TO_DB: Record<CareFeatureKey, string> = {
    careStation: 'care_station',
    careResidents: 'care_residents',
    careHandover: 'care_handover',
    careRoster: 'care_roster',
    careInventory: 'care_inventory',
    careInbox: 'care_inbox',
    careWoundMgmt: 'care_wound_mgmt',
    careMedications: 'care_medications',
    careSisDocumentation: 'care_sis_documentation',
    careAdmissions: 'care_admissions',
    careOccupancy: 'care_occupancy',
    careContacts: 'care_contacts',
    carePracticeRequests: 'care_practice_requests',
    careFamilyPortal: 'care_family_portal',
    careAnalytics: 'care_analytics',
};

/** Plan-Feature-Matrix: welche Features in welchem Plan enthalten */
export const CARE_PLAN_FEATURES: Record<CareSubscriptionPlan, CareFeatureKey[]> = {
    none: [],
    starter: [
        'careStation',
        'careResidents',
        'careHandover',
        'careRoster',
        'careInbox',
        'careContacts',
        'carePracticeRequests',
    ],
    professional: [
        'careStation',
        'careResidents',
        'careHandover',
        'careRoster',
        'careInbox',
        'careContacts',
        'carePracticeRequests',
        'careInventory',
        'careWoundMgmt',
        'careMedications',
        'careSisDocumentation',
        'careAdmissions',
        'careOccupancy',
    ],
    enterprise: [
        'careStation',
        'careResidents',
        'careHandover',
        'careRoster',
        'careInbox',
        'careContacts',
        'carePracticeRequests',
        'careInventory',
        'careWoundMgmt',
        'careMedications',
        'careSisDocumentation',
        'careAdmissions',
        'careOccupancy',
        'careFamilyPortal',
        'careAnalytics',
    ],
};

/** Feature-Metadaten für UI (Label, Beschreibung, ROI) */
export const CARE_FEATURE_METADATA: Record<CareFeatureKey, { label: string; description: string; roi: string }> = {
    careStation: { label: 'Station', description: 'Stations-Übersicht und Dashboard', roi: 'Kern-Feature' },
    careResidents: { label: 'Bewohner', description: 'Bewohnerverwaltung und Akten', roi: 'Kern-Feature' },
    careHandover: { label: 'Übergabe', description: 'Digitales Übergabebuch', roi: 'Kern-Feature' },
    careRoster: { label: 'Dienstplan', description: 'Dienstplanung und Schichtverwaltung', roi: '~15 Min/Tag weniger Planungsaufwand' },
    careInventory: { label: 'Lagerverwaltung', description: 'Material- und Medikamentenlager', roi: '~20% weniger Fehlbestände' },
    careInbox: { label: 'Postfach', description: 'Interne Kommunikation', roi: 'Schnellere Abstimmung im Team' },
    careWoundMgmt: { label: 'Wundmanagement', description: 'Wunddokumentation mit Fotoverlauf', roi: 'MDK-sichere Dokumentation' },
    careMedications: { label: 'Medikamente', description: 'Medikationsverwaltung und eMAR', roi: 'Weniger Medikationsfehler' },
    careSisDocumentation: { label: 'SIS-Dokumentation', description: 'Strukturierte Informationssammlung', roi: 'Standardkonforme Pflegeplanung' },
    careAdmissions: { label: 'Aufnahmen', description: 'Bewohner-Aufnahme-Workflow', roi: 'Schnellerer Aufnahmeprozess' },
    careOccupancy: { label: 'Belegung', description: 'Bettenbelegung und -planung', roi: 'Optimierte Auslastung' },
    careContacts: { label: 'Kontakte', description: 'Kontaktverwaltung (Ärzte, Angehörige)', roi: 'Zentrales Adressbuch' },
    carePracticeRequests: { label: 'Praxisanfragen', description: 'Arztanfragen und -koordination', roi: 'Schnellere Arzt-Pflege-Abstimmung' },
    careFamilyPortal: { label: 'Angehörigenportal', description: 'Familien-Zugang zu Bewohnerinfos', roi: 'Höhere Angehörigen-Zufriedenheit' },
    careAnalytics: { label: 'Auswertungen', description: 'Qualitäts- und Leistungskennzahlen', roi: 'Datenbasierte Qualitätsverbesserung' },
};

export interface CareFeatureStatus {
    enabled: boolean;
    inPlan: boolean;
    configurable: boolean;
    lockedReason?: string;
}

export type CareFeatureStatusMap = Record<CareFeatureKey, CareFeatureStatus>;
